import { useMemo, useState } from "react";
import {Search} from 'lucide-react';
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { faqs as staticFaqs } from "@/data/content";
import { useFaqs } from "@/hooks/useFaqs";

const CATEGORIES = ["Tous", "Candidats", "Employeurs", "Formation", "BNSSA", "MNS"] as const;

interface DisplayFaq {
  id: string;
  question: string;
  answer: string;
  category: string;
  isHtml: boolean;
}

export default function Faq() {
  const [category, setCategory] = useState<string>("Tous");
  const [search, setSearch] = useState("");
  const { items: dbFaqs, loading } = useFaqs();

  // Source de vérité : si la base contient des FAQ, on les utilise (HTML enrichi).
  // Sinon, fallback sur le dataset statique pour ne jamais afficher une page vide.
  const allFaqs = useMemo<DisplayFaq[]>(() => {
    if (dbFaqs.length > 0) {
      return dbFaqs
        .filter((f) => f.isActive !== false)
        .sort((a, b) => {
          if (a.category !== b.category) return a.category.localeCompare(b.category);
          return a.order - b.order;
        })
        .map((f) => ({
          id: f._id,
          question: f.question,
          answer: f.answer,
          category: f.category,
          isHtml: true,
        }));
    }
    return staticFaqs.map((f) => ({
      id: f.id,
      question: f.question,
      answer: f.answer,
      category: f.category,
      isHtml: false,
    }));
  }, [dbFaqs]);

  const filtered = useMemo(() => {
    return allFaqs.filter((f) => {
      if (category !== "Tous" && f.category !== category) return false;
      if (search) {
        const s = search.toLowerCase();
        // Pour HTML on retire les balises pour la recherche
        const plainAnswer = f.isHtml ? f.answer.replace(/<[^>]+>/g, ' ') : f.answer;
        return f.question.toLowerCase().includes(s) || plainAnswer.toLowerCase().includes(s);
      }
      return true;
    });
  }, [allFaqs, category, search]);

  const faqPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": "FAQ — Emplois BNSSA & MNS",
    "url": "https://bnssa-mns.fr/faq",
    "mainEntity": allFaqs.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": {
        "@type": "Answer",
        // JSON-LD doit contenir du texte plat — on retire les balises
        "text": f.isHtml ? f.answer.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : f.answer
      }
    }))
  };

  const faqBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://bnssa-mns.fr/" },
    { "@type": "ListItem", "position": 2, "name": "FAQ", "item": "https://bnssa-mns.fr/faq" }]

  };

  return (
    <Layout>
      <Seo
        title="FAQ BNSSA & MNS — Questions fréquentes | bnssa-mns.fr"
        description="Toutes les réponses à vos questions sur les métiers BNSSA et MNS : formation, salaire, contrats, candidature, recrutement."
        canonical="https://bnssa-mns.fr/faq"
        jsonLd={{ "@context": "https://schema.org", "@graph": [faqPageJsonLd, faqBreadcrumb] }} />


      <section className="bg-gradient-to-br from-gray-900 to-slate-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-3">Questions fréquentes</h1>
          <p className="text-lg text-gray-300 mb-6">Vous avez une question sur les métiers de la surveillance et de l'enseignement aquatiques, la candidature ou le recrutement ? Trouvez votre réponse ici.

          </p>
          <div className="bg-white p-2 rounded-xl shadow-lg flex items-center gap-2">
            <Search className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" />
            <input
              type="text"
              placeholder="Rechercher une question..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 outline-none text-gray-900 text-sm bg-transparent py-2"
              aria-label="Rechercher une question" />

          </div>
        </div>
      </section>

      <section className="py-10 sticky top-16 z-30 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) =>
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
              category === c ?
              "bg-gray-900 text-white" :
              "bg-gray-100 text-gray-700 hover:bg-gray-200"}`
              }>

                {c}
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && allFaqs.length === 0 ?
          <div className="text-center py-12 text-gray-600">
            Chargement des questions…
          </div> :
          filtered.length === 0 ?
          <div className="text-center py-12 text-gray-600">
              Aucune question correspondante. Essayez d'autres mots-clés.
            </div> :

          <div className="space-y-3">
              {filtered.map((f) =>
            <details
              key={f.id}
              className="bg-white border border-gray-200 rounded-xl p-5 group">

                  <summary className="font-semibold text-gray-900 cursor-pointer list-none flex items-start justify-between gap-4">
                    <span className="flex-1">{f.question}</span>
                    <span className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 group-open:rotate-45 transition-transform flex-shrink-0 text-lg">+</span>
                  </summary>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-semibold rounded mb-2">
                      {f.category}
                    </span>
                    {f.isHtml ? (
                      <div
                        className="prose-rte text-gray-700 text-sm leading-relaxed [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_a]:text-blue-600 [&_a]:underline [&_strong]:font-bold [&_h2]:text-base [&_h2]:font-bold [&_h2]:mt-2 [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-bold [&_h3]:mt-2 [&_h3]:mb-1"
                        dangerouslySetInnerHTML={{ __html: f.answer }}
                      />
                    ) : (
                      <p className="text-gray-700 text-sm leading-relaxed">{f.answer}</p>
                    )}
                  </div>
                </details>
            )}
            </div>
          }
        </div>
      </section>
    </Layout>);

}
